import React, { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  MenuDietaryPreferences,
  type AllergenOrDietaryRestriction,
} from '@sg/shared-fe/features/menu';

import { AccountScreenContainer, AccountScreenSection, AccountScreenTitle } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useDietaryPreferencesMachine } from '../../context/DietaryPreferences';

export const AccountDietaryRestrictionsScreen = () => {
  const { formatMessage } = useIntl();
  const isLoggedIn = useIsLoggedIn();

  const containerA11ySelector = useA11ySelector('account.dietary-restrictions.container');

  // ─────────────────────────────────────────────────────────────────────

  const {
    isIdle,
    sortedRestrictionsNames,
    pendingRestrictions,
    isUpdatingRemoteRestrictions,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    confirmChanges,
    toggleRestriction,
    cancelChanges,
    start,
  } = useDietaryPreferencesMachine({ referrer: 'AccountDietaryRestrictions' });

  // ─── Handlers ────────────────────────────────────────────────────────

  const startDietaryPreferencesMachineOnMount = useCallback(() => {
    if (!isLoggedIn) return;
    if (!isIdle) return;

    start({ stayActiveAfterConfirm: true });

    // cancel pending changes on screen blur
    return cancelChanges;
  }, [cancelChanges, isIdle, isLoggedIn, start]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(startDietaryPreferencesMachineOnMount);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="dietary-restrictions" {...containerA11ySelector.props}>
      <AccountScreenTitle title={formatMessage(messages.title)} />

      <AccountScreenSection>
        <MenuDietaryPreferences.Container withoutHorizontalSpacing>
          <MenuDietaryPreferences.Description />

          <MenuDietaryPreferences.ButtonsContainer>
            {sortedRestrictionsNames.map((restriction) => (
              <MenuDietaryPreferences.Button<AllergenOrDietaryRestriction>
                key={restriction}
                restriction={restriction}
                isLoading={isFetchingCustomerPreferences}
                isChecked={pendingRestrictions[restriction]}
                isDisabled={isUpdatingRemoteRestrictions}
                toggleRestriction={toggleRestriction}
              />
            ))}
          </MenuDietaryPreferences.ButtonsContainer>

          <MenuDietaryPreferences.Notices />

          <MenuDietaryPreferences.SubmitButton
            isLoading={isUpdatingRemoteRestrictions}
            isDisabled={!hasUnsavedPreferences}
            onPress={confirmChanges}
          />
        </MenuDietaryPreferences.Container>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Dietary restrictions',
    description: 'Dietary Restrictions | Title',
  },
});
